import { useCallback, useEffect, useRef } from 'react';
import { api, parseApiError } from '../utils/api';
import { toast } from 'sonner';
import { useApiQuery } from './useApiQuery';
import { automationOutcome } from '../utils/automationOutcome';
import { approveActionBatches } from '../utils/pendingActionBatches';

/**
 * Hook for managing Caleb's pending actions (approve/reject workflow).
 *
 * Provides:
 * - actions: list of pending actions
 * - pendingCount: badge count of approvals and unresolved executions
 * - approve / reject / bulkApprove / bulkReject mutations
 * - loading states
 * - event-driven refresh via SSE (with 2-min safety-net poll)
 *
 * @param {object} options
 * @param {boolean} options.enabled - Whether to fetch (default: true)
 * @param {boolean} options.actionsEnabled - Whether to fetch the action list
 * @param {string|null} options.status - Action status filter
 * @returns {object}
 */
export function usePendingActions({
  enabled = true,
  actionsEnabled = false,
  status = 'pending',
} = {}) {
  const statusParam = status ? `&status=${encodeURIComponent(status)}` : '';
  const actionsUrl = `/pending-actions?limit=50${statusParam}`;

  const {
    data: actionsData,
    loading,
    refetch: fetchActions,
    setData: setActions,
  } = useApiQuery(actionsUrl, {
    enabled: enabled && actionsEnabled,
    showErrorToast: true,
    staleTime: 15_000,
    transform: (payload) => payload?.data?.actions ?? [],
  });

  const {
    data: pendingCountData,
    loading: countLoading,
    refetch: fetchCount,
    setData: setPendingCount,
  } = useApiQuery('/pending-actions/count', {
    enabled,
    showErrorToast: false,
    staleTime: 15_000,
    refetchInterval: 120_000,
    transform: (payload) => payload?.data?.count ?? 0,
  });
  const actions = actionsData ?? [];
  const pendingCount = pendingCountData ?? 0;

  // Approve a single action
  const approve = useCallback(async (actionId) => {
    try {
      const res = await api.post(`/pending-actions/${actionId}/approve`);
      window.dispatchEvent(new Event('caleb:actions-changed'));
      const result = res.data?.data ?? {};
      if (result.success === false) {
        const nextStatus = result.status ?? (
          result.error_code === 'EXECUTION_OUTCOME_UNKNOWN'
            ? 'execution_unknown'
            : 'failed'
        );
        setActions(prev => (prev ?? []).map(a => (
          a.id === actionId
            ? {
                ...a,
                status: nextStatus,
                error: result.error ?? 'Execution failed',
                result: result.result ?? null,
              }
            : a
        )));
        if (nextStatus !== 'execution_unknown') {
          setPendingCount(prev => Math.max(0, (prev ?? 0) - 1));
        }
        toast.error(result.error ?? 'Action approval failed');
        return res.data;
      }
      const deferred = result.deferred;
      toast.success(
        automationOutcome(result.result)?.message || (deferred
          ? 'Action approved and scheduled after the health model update'
          : 'Action approved and executed'),
      );
      // Optimistically remove from list
      setActions(prev => (prev ?? []).filter(a => a.id !== actionId));
      setPendingCount(prev => Math.max(0, (prev ?? 0) - 1));
      return res.data;
    } catch (err) {
      toast.error(parseApiError(err));
      throw err;
    }
  }, [setActions, setPendingCount]);

  // Reject a single action
  const reject = useCallback(async (actionId) => {
    try {
      await api.post(`/pending-actions/${actionId}/reject`);
      window.dispatchEvent(new Event('caleb:actions-changed'));
      toast.success('Action rejected');
      setActions(prev => (prev ?? []).filter(a => a.id !== actionId));
      setPendingCount(prev => Math.max(0, (prev ?? 0) - 1));
    } catch (err) {
      toast.error(parseApiError(err));
      throw err;
    }
  }, [setActions, setPendingCount]);

  // Bulk approve
  const bulkApprove = useCallback(async (actionIds) => {
    try {
      const summary = await approveActionBatches(actionIds, {
        onBatch: (results) => {
          const completedIds = new Set(results
            .filter(result => result.success || result.status === 'failed')
            .map(result => result.action_id));
          setActions(prev => (prev ?? []).filter(action => !completedIds.has(action.id)));
          setPendingCount(prev => Math.max(0, (prev ?? 0) - completedIds.size));
          window.dispatchEvent(new Event('caleb:actions-changed'));
        },
      });
      if (summary.succeeded) toast.success(`${summary.succeeded} actions approved`);
      if (summary.failed > 0) toast.error(`${summary.failed} actions were not approved`);
      return { success: true, data: summary };
    } catch (err) {
      toast.error(parseApiError(err));
      throw err;
    }
  }, [setActions, setPendingCount]);

  // Bulk reject
  const bulkReject = useCallback(async (actionIds) => {
    try {
      const res = await api.post('/pending-actions/bulk-reject', { action_ids: actionIds });
      window.dispatchEvent(new Event('caleb:actions-changed'));
      const summary = res.data?.data ?? {};
      const succeededIds = new Set(
        (summary.results ?? []).filter(result => result.success).map(result => result.action_id)
      );
      toast.success(`${summary.succeeded ?? succeededIds.size} actions rejected`);
      setActions(prev => (prev ?? []).filter(a => !succeededIds.has(a.id)));
      setPendingCount(prev => Math.max(0, (prev ?? 0) - succeededIds.size));
      if (summary.failed > 0) toast.error(`${summary.failed} actions were not rejected`);
      return res.data;
    } catch (err) {
      toast.error(parseApiError(err));
      throw err;
    }
  }, [setActions, setPendingCount]);

  // Update parameters for a pending action (e.g. edited campaign emails)
  const updateParameters = useCallback(async (actionId, parameters) => {
    try {
      const res = await api.patch(`/pending-actions/${actionId}`, { parameters });
      window.dispatchEvent(new Event('caleb:actions-changed'));
      const savedParameters = res.data?.data?.parameters ?? parameters;
      toast.success('Changes saved');
      // Update local state
      setActions(prev => (prev ?? []).map(a =>
        a.id === actionId ? { ...a, parameters: savedParameters } : a
      ));
      return res.data;
    } catch (err) {
      toast.error(parseApiError(err));
      throw err;
    }
  }, [setActions]);

  const resolveExecution = useCallback(async (actionId, outcome, note) => {
    try {
      const res = await api.post(`/pending-actions/${actionId}/resolve-execution`, {
        outcome,
        note,
      });
      window.dispatchEvent(new Event('caleb:actions-changed'));
      setActions(prev => (
        status === 'execution_unknown'
          ? (prev ?? []).filter(a => a.id !== actionId)
          : (prev ?? []).map(a => (
              a.id === actionId
                ? { ...a, status: outcome, execution_resolution_note: note }
                : a
            ))
      ));
      setPendingCount(prev => Math.max(0, (prev ?? 0) - 1));
      toast.success(outcome === 'executed' ? 'Action marked as executed' : 'Action marked as failed');
      return res.data;
    } catch (err) {
      toast.error(parseApiError(err));
      throw err;
    }
  }, [setActions, setPendingCount, status]);

  const sseDebounceRef = useRef(null);
  useEffect(() => {
    if (!enabled) return undefined;

    const handleNotification = () => {
      if (sseDebounceRef.current) clearTimeout(sseDebounceRef.current);
      sseDebounceRef.current = setTimeout(() => {
        fetchCount();
        if (actionsEnabled) fetchActions();
        sseDebounceRef.current = null;
      }, 500);
    };
    window.addEventListener('sse:notification', handleNotification);
    window.addEventListener('caleb:actions-changed', handleNotification);

    return () => {
      window.removeEventListener('sse:notification', handleNotification);
      window.removeEventListener('caleb:actions-changed', handleNotification);
      if (sseDebounceRef.current) clearTimeout(sseDebounceRef.current);
    };
  }, [enabled, actionsEnabled, fetchActions, fetchCount]);

  return {
    actions,
    pendingCount,
    loading,
    countLoading,
    fetchActions,
    fetchCount,
    approve,
    reject,
    bulkApprove,
    bulkReject,
    updateParameters,
    resolveExecution,
  };
}

export default usePendingActions;
